import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  MessageCircle,
  Trash2,
  BookOpen,
  Sparkles,
  AlertTriangle,
  Send,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { BookClubDiscussion, BookClubDiscussionReply } from '@/types/chat';
import { useAuth } from '@/hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';

interface DiscussionCardProps {
  discussion: BookClubDiscussion;
  onDelete: (id: string) => Promise<void>;
  onFetchReplies: (discussionId: string) => Promise<BookClubDiscussionReply[]>;
  onAddReply: (discussionId: string, content: string) => Promise<void>;
  onDeleteReply: (replyId: string) => Promise<void>;
}

export function DiscussionCard({
  discussion,
  onDelete,
  onFetchReplies,
  onAddReply,
  onDeleteReply,
}: DiscussionCardProps) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(!discussion.is_spoiler);
  const [replies, setReplies] = useState<BookClubDiscussionReply[]>([]);
  const [repliesLoaded, setRepliesLoaded] = useState(false);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const isOwn = user?.id === discussion.created_by;
  const isChapter = discussion.type === 'chapter';

  const handleToggle = async () => {
    const next = !expanded;
    setExpanded(next);

    if (next && !repliesLoaded) {
      setLoadingReplies(true);
      const data = await onFetchReplies(discussion.id);
      setReplies(data);
      setRepliesLoaded(true);
      setLoadingReplies(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    try {
      await onAddReply(discussion.id, replyText.trim());
      setReplyText('');
      // Refresh replies
      const data = await onFetchReplies(discussion.id);
      setReplies(data);
    } catch {
      // handled by hook
    } finally {
      setSending(false);
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    await onDeleteReply(replyId);
    setReplies(prev => prev.filter(r => r.id !== replyId));
  };

  const chapterLabel =
    discussion.chapter_start && discussion.chapter_end
      ? `Ch. ${discussion.chapter_start}–${discussion.chapter_end}`
      : discussion.chapter_start
        ? `Ch. ${discussion.chapter_start}`
        : null;

  return (
    <div className="border border-border/50 rounded-2xl overflow-hidden bg-card shadow-soft">
      {/* Header — always visible */}
      <button
        onClick={handleToggle}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="mt-0.5">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {isChapter ? (
              <Badge variant="outline" className="gap-1 text-xs">
                <BookOpen className="w-3 h-3" />
                {chapterLabel || 'Chapter'}
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1 text-xs">
                <Sparkles className="w-3 h-3" />
                Prompt
              </Badge>
            )}
            {discussion.is_spoiler && !expanded && (
              <Badge variant="outline" className="gap-1 text-xs text-destructive border-destructive/30">
                <AlertTriangle className="w-3 h-3" />
                Spoiler
              </Badge>
            )}
          </div>

          {discussion.is_spoiler && !expanded ? (
            <p className="text-sm font-medium text-muted-foreground italic">
              Tap to reveal (may contain spoilers)
            </p>
          ) : (
            <p className="text-sm font-medium text-foreground">{discussion.title}</p>
          )}

          <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
            <span>{discussion.creator?.name || discussion.creator?.username || 'Member'}</span>
            <span>·</span>
            <span>{formatDistanceToNow(new Date(discussion.created_at), { addSuffix: true })}</span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <MessageCircle className="w-3 h-3" />
              {discussion.reply_count || 0}
            </span>
          </div>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-border/30">
          {/* Description */}
          {discussion.description && (
            <div className="px-4 pt-3 pb-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {discussion.description}
              </p>
            </div>
          )}

          {/* Replies */}
          <div className="px-4 pb-3">
            {loadingReplies ? (
              <p className="text-xs text-muted-foreground py-3 text-center">Loading replies...</p>
            ) : replies.length > 0 ? (
              <div className="space-y-3 mt-2">
                {replies.map(reply => (
                  <div key={reply.id} className="flex gap-2.5 group">
                    <Avatar className="h-6 w-6 mt-0.5">
                      <AvatarImage src={reply.user?.image || undefined} />
                      <AvatarFallback className="text-[10px]">
                        {reply.user?.name?.charAt(0) || reply.user?.username?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium">{reply.user?.name || reply.user?.username}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}
                        </span>
                        {reply.user_id === user?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteReply(reply.id)}
                          >
                            <Trash2 className="w-3 h-3 text-destructive" />
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-foreground mt-0.5">{reply.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-3 text-center">
                No replies yet — be the first to respond!
              </p>
            )}

            {/* Reply input */}
            <div className="flex gap-2 mt-3">
              <Input
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply..."
                className="text-sm h-9"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
              />
              <Button
                size="sm"
                className="h-9 px-3"
                disabled={!replyText.trim() || sending}
                onClick={handleSendReply}
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Delete button for creator */}
          {isOwn && (
            <div className="border-t border-border/30 px-4 py-2 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive gap-1.5 text-xs"
                onClick={() => onDelete(discussion.id)}
              >
                <Trash2 className="w-3 h-3" />
                Delete
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
